// src/pages/UploadPage.js

/**
 * UploadPage – Advanced PDF upload interface with real-time progress tracking
 * and smart data management for DocumentAI processing.
 * 
 * Architecture:
 * - Hybrid data fetching: Real-time Firestore listeners for In-Progress + API pagination for others
 * - Smart debounced refresh system for optimal performance during batch uploads
 * - Real-time count listeners for all status tabs with automatic UI updates
 * 
 * Core Features:
 * - Drag-and-drop + browse file selection with validation (size, type, duplicates)
 * - Multi-model support (Shipping Labels, Safety Datasheets)
 * - Real-time progress tracking with live status updates
 * - Smart pagination with cursor-based navigation
 * - Status-based filtering (In-Progress, Completed, Error, Queued)
 * - Batch upload support with debounced UI updates
 * 
 * Data Flow:
 * - File validation via `processFiles` (size limits, PDF type, duplicates)
 * - Parallel upload processing via `handleUpload` with error handling
 * - Real-time listeners for In-Progress status with immediate UI updates
 * - Debounced API refreshes for other statuses to prevent flooding
 * - Automatic count updates via Firestore listeners for all statuses
 * 
 * State Management:
 * - File & UI: selectedFiles, isDragActive, uploadSuccess, selectedModel
 * - Progress Tracking: uploads, uploadCounts, statusFilter
 * - Table Pagination: currentTablePage, tablePageSize, totalTablePages, totalUploadCount
 * - Debounce: queuedRefreshTimeout, completedRefreshTimeout, errorRefreshTimeout
 * 
 * Key Functions:
 * - `fetchFilteredUploads`: API-based pagination for non-In-Progress statuses
 * - `processFiles`: File validation and categorization
 * - `handleUpload`: Main upload logic with parallel processing
 * - Real-time listeners: In-Progress tracking + count updates
 * - Debounced refreshes: Queued (1s), Completed/Error (300ms)
 * 
 * Props:
 * - `setServerResponse` (optional): Callback for structured JSON results
 * 
 * Author: Galo Alonso Peralta Espinoza
 * Date: 07-15-2025
 */

/**
 * ## TODOs:
 * ## - Add a file splitter for files > 32 MB
 */

//## ======================= IMPORTS ======================= ##//
import React, { useState, useRef, useEffect } from 'react'

// Components
import Navbar from './components/Navbar'
import PaywallModal from './components/PaywallModal'

// Material UI
import { Typography, Tooltip } from '@mui/material'

// Icons
import UploadFileIcon from '@mui/icons-material/UploadFile'
import ClearIcon from '@mui/icons-material/Clear'
import WarningIcon from '@mui/icons-material/Warning'
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'

// Firebase
import { auth, db } from '../firebaseConfig'
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore'

// Authentication helpers
import { authenticatedFetch, handleAuthError } from '../utils/authHelpers'

// PDF processing
import * as pdfjsLib from 'pdfjs-dist'

// Local CSS
import styles from './UploadPage.module.css'

// Configure PDF.js worker - using local worker file to avoid CORS issues
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

//## ======================= MAIN COMPONENT ======================= ##//
function UploadPage({ setServerResponse }) {

  //## ======================= STATE MANAGEMENT ======================= ##//
  
  // File & UI States
  const [selectedFiles, setSelectedFiles] = useState([])
  const [totalPdfPages, setTotalPdfPages] = useState(0)
  const [isDragActive, setIsDragActive] = useState(false)
  const fileInputRef = useRef(null)
  const [uploadSuccess, setUploadSuccess] = useState(false)
  const [selectedModel, setSelectedModel] = useState('')

  // Progress Tracking States
  const [uploads, setUploads] = useState([])
  
  // Table Pagination States
  const [currentTablePage, setCurrentTablePage] = useState(0)
  const [tablePageSize, setTablePageSize] = useState(20)
  const [totalTablePages, setTotalTablePages] = useState(0)
  const [totalUploadCount, setTotalUploadCount] = useState(0)

  // Filter States
  const [statusFilter, setStatusFilter] = useState('In-Progress')
  const [uploadCounts, setUploadCounts] = useState({ total: 0, byStatus: {} })

  // Subscription State
  const [showPaywallModal, setShowPaywallModal] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false)

  // Page Limit States
  const [pageLimit, setPageLimit] = useState(0)
  const [pagesUsed, setPagesUsed] = useState(0)
  const [remainingPages, setRemainingPages] = useState(0)

  // Document Limit States
  const [documentsLimit, setDocumentsLimit] = useState(0)
  const [documentsUsed, setDocumentsUsed] = useState(0)
  const [remainingDocuments, setRemainingDocuments] = useState(0)

  // Debounce refs for tab refreshes
  const queuedRefreshTimeout = useRef();
  const completedRefreshTimeout = useRef();
  const errorRefreshTimeout = useRef();

  //## ======================= API FUNCTIONS ======================= ##//
  
  //** fetchFilteredUploads **//
  // Fetch paginated upload data from backend API for non-In-Progress statuses
  const fetchFilteredUploads = async (page = 0) => {
    try {
      //? Make API request for paginated upload data with authentication
      const response = await authenticatedFetch(`/api/get-uploads?status=${statusFilter}&page=${page}&limit=${tablePageSize}&includeCount=true`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch uploads: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      //? Transform backend data to frontend format
      const transformedUploads = data.uploads.map(upload => ({
        ...upload,
        status: transformStatus(upload.status),
        progress: upload.progress ?? 0
      }));
      
      //? Update state with transformed data and pagination info
      setUploads(transformedUploads);
      setTotalTablePages(data.totalPages);
      setTotalUploadCount(data.totalCount);
      
    } catch (err) {
      console.error('Error fetching filtered uploads:', err);
      
      //? Handle different types of errors appropriately
      if (err.message.includes('not authenticated') || 
          err.message.includes('Authentication expired') ||
          err.message.includes('Session expired') ||
          err.message.includes('Authentication failed')) {
        //? Authentication errors - use handleAuthError
        const userMessage = handleAuthError(err, 'fetch uploads');
        //? Note: authenticatedFetch will handle redirects automatically
      } else if (err.message.includes('Failed to fetch') || 
                 err.message.includes('NetworkError') ||
                 err.message.includes('fetch')) {
        //? Network errors - user-friendly message
        console.error('Network error fetching uploads:', err);
        //? Keep existing data on error to maintain UI stability
        //? Could show a toast notification: "Network error. Please check your connection."
      } else {
        //? Other errors (server errors, etc.) - generic message
        console.error('Server error fetching uploads:', err);
        //? Keep existing data on error to maintain UI stability
        //? Could show a toast notification: "Unable to load uploads. Please try again."
      }
      
      //? Keep existing data on error to maintain UI stability
      //? Note: authenticatedFetch will handle redirects automatically
    }
  };

  //## ======================= UTILITY FUNCTIONS ======================= ##//
  
  //** transformStatus **//
  // Convert backend status values to frontend display format
  const transformStatus = (status) => {
    if (!status) return 'N/A';
    
    switch (status) {
      case 'processing':
        return 'In-Progress';
      case 'completed':
        return 'Completed';
      default:
        return status;
    }
  };

  //## ======================= DEBOUNCED REFRESH FUNCTIONS ======================= ##//
  
  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (queuedRefreshTimeout.current) clearTimeout(queuedRefreshTimeout.current);
      if (completedRefreshTimeout.current) clearTimeout(completedRefreshTimeout.current);
      if (errorRefreshTimeout.current) clearTimeout(errorRefreshTimeout.current);
    };
  }, []);

  //** debouncedRefreshQueued **//
  // Debounced API refresh for Queued tab (1 second delay for batch uploads)
  const debouncedRefreshQueued = () => {
    if (queuedRefreshTimeout.current) clearTimeout(queuedRefreshTimeout.current);
    queuedRefreshTimeout.current = setTimeout(() => {
      if (statusFilter === 'Queued') fetchFilteredUploads(currentTablePage);
    }, 1000); // 1 second for batch uploads
  };

  //** debouncedRefreshCompleted **//
  // Debounced API refresh for Completed tab (300ms delay for status changes)
  const debouncedRefreshCompleted = () => {
    if (completedRefreshTimeout.current) clearTimeout(completedRefreshTimeout.current);
    completedRefreshTimeout.current = setTimeout(() => {
      if (statusFilter === 'Completed') fetchFilteredUploads(currentTablePage);
    }, 300); // 300ms for status changes
  };

  //** debouncedRefreshError **//
  // Debounced API refresh for Error tab (300ms delay for status changes)
  const debouncedRefreshError = () => {
    if (errorRefreshTimeout.current) clearTimeout(errorRefreshTimeout.current);
    errorRefreshTimeout.current = setTimeout(() => {
      if (statusFilter === 'Error') fetchFilteredUploads(currentTablePage);
    }, 300); // 300ms for status changes
  };

  //## ======================= PAGINATION FUNCTIONS ======================= ##//
  
  //** handlePageChange **//
  // Simple page navigation
  const handlePageChange = (newPage) => {
    setCurrentTablePage(newPage);
  };

  // Reset to first page when filter changes
  useEffect(() => {
    setCurrentTablePage(0);
  }, [statusFilter]);

  // Reset to first page if current page is out of bounds
  useEffect(() => {
    if (currentTablePage >= totalTablePages && totalTablePages > 0) {
      setCurrentTablePage(Math.max(0, totalTablePages - 1));
    }
  }, [totalTablePages, currentTablePage]);

  //## ======================= REACT EFFECTS ======================= ##//

  //** Simplified Real-time Firestore Listener (In-Progress only) **//
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    // Only set up real-time listener for In-Progress uploads
    if (statusFilter === 'In-Progress') {
      const uploadsRef = collection(db, 'uploads');
      
      // Simple query for all In-Progress uploads
      const inProgressQuery = query(
        uploadsRef,
        where('userId', '==', user.uid),
        where('status', 'in', ['In-Progress', 'processing']),
        orderBy('createdAt', 'desc')
      );

      const unsubscribe = onSnapshot(
        inProgressQuery,
        (snapshot) => {
          const inProgressUploads = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            status: transformStatus(doc.data().status),
            progress: doc.data().progress ?? 0
          }));
          
          setUploads(inProgressUploads);
        },
        (error) => {
          console.error('Firestore listener error:', error);
        }
      );

      return () => unsubscribe();
      
    } else {
      // For other filters, fetch via API
      fetchFilteredUploads(currentTablePage);
    }
  }, [statusFilter, currentTablePage]);

  //** Real-time count listeners for all statuses **//
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    const uploadsRef = collection(db, 'uploads');
    const statuses = ['Queued', 'In-Progress', 'Completed', 'Error', 'Failed'];
    const unsubscribes = [];

    statuses.forEach(status => {
      const countQuery = query(
        uploadsRef,
        where('userId', '==', user.uid),
        where('status', '==', status)
      );
      const unsubscribe = onSnapshot(countQuery, (snapshot) => {
        setUploadCounts(prev => {
          const newCounts = { ...prev.byStatus };
          
          if (status === 'Failed') {
            // Add Failed count to Error count
            newCounts['Error'] = (newCounts['Error'] || 0) + snapshot.size;
          } else {
            newCounts[status] = snapshot.size;
          }
          
          return {
            ...prev,
            byStatus: newCounts
          };
        });
      });
      unsubscribes.push(unsubscribe);
    });

    return () => {
      unsubscribes.forEach(unsub => unsub());
    };
  }, []);

  //## ======================= SUBSCRIPTION CHECK ======================= ##//
  
  // Check subscription status on page load
  useEffect(() => {
    const checkSubscription = async () => {
      try {
        const response = await authenticatedFetch('/api/user/subscription-status');
        
        if (response.ok) {
          const { subscription } = await response.json();
          setShowPaywallModal(subscription.status === 'inactive' || !subscription.status);
          setIsAdmin(subscription?.tier === 'admin')
        } else {
          // If we can't verify subscription, show modal for safety
          setShowPaywallModal(true);
        }
      } catch (error) {
        console.error('Subscription check failed:', error);
        // Show modal if subscription check fails
        setShowPaywallModal(true);
      }
    };

    checkSubscription();
  }, []);

  // Calculate total PDF pages whenever selectedFiles changes
  useEffect(() => {
    const total = selectedFiles.reduce((sum, file) => sum + (file.pageCount || 0), 0);
    setTotalPdfPages(total);
  }, [selectedFiles]);

  // Fetch page limits on component mount
  useEffect(() => {
    const fetchPageLimits = async () => {
      try {
        const response = await authenticatedFetch('/api/user/page-limits');
        if (response.ok) {
          const data = await response.json();
          setPageLimit(data.pageLimit || 0);
          setPagesUsed(data.pagesUsed || 0);
          setRemainingPages(data.remaining || 0);
        } else {
          console.error('Failed to fetch page limits:', response.statusText);
        }
      } catch (error) {
        console.error('Error fetching page limits:', error);
      }
    };

    fetchPageLimits();
  }, []);

  // Fetch document limits on component mount
  useEffect(() => {
    const fetchDocumentLimits = async () => {
      try {
        const response = await authenticatedFetch('/api/user/document-limits');
        if (response.ok) {
          const data = await response.json();
          setDocumentsLimit(data.documentsLimit);
          setDocumentsUsed(data.documentsUsed || 0);
          setRemainingDocuments(data.remaining || 0);
        } else {
          console.error('Failed to fetch document limits:', response.statusText);
        }
      } catch (error) {
        console.error('Error fetching document limits:', error);
      }
    };

    fetchDocumentLimits();
  }, []);

  //## ======================= FILE HANDLING FUNCTIONS ======================= ##//
  
  // File size constants
  const MAX_SIZE_MB = 31; // Safety
  const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;
  const MAX_PAGES_PER_DOCUMENT = 20; // New document page limit

  //** validateDocumentPages **//
  // Check if any document exceeds 20-page limit
  const validateDocumentPages = () => {
    // Admin bypass: If documentsLimit is null, skip page validation
    if (documentsLimit === null) return false;
    if (isAdmin) return false;
    
    return selectedFiles.some(file => (file.pageCount || 0) > MAX_PAGES_PER_DOCUMENT);
  };

  //** isUploadDisabledByDocuments **//
  // Check if upload should be disabled based on document limits (New validation logic)
  const isUploadDisabledByDocuments = () => {
    // Basic conditions
    if (selectedFiles.length === 0 || !selectedModel) {
      return true;
    }
    
    // Check if any document exceeds 20 pages (has admin bypass built-in)
    if (validateDocumentPages()) {
      return true;
    }
    
    // Document limit check (automatically bypassed for admins since documentsLimit is null)
    if (documentsLimit > 0) {
      const totalDocumentsAfterUpload = documentsUsed + selectedFiles.length;
      
      if (totalDocumentsAfterUpload > documentsLimit) {
        return true;
      }
    }
    
    return false;
  };

  //** isUploadDisabledByPages **//
  // Check if upload should be disabled based on various conditions (Original page-based logic)
  const isUploadDisabledByPages = () => {
    // Basic conditions
    if (selectedFiles.length === 0 || !selectedModel) {
      return true;
    }
    
    // Page limit check
    if (pageLimit > 0) {
      const currentFilesTotalPages = selectedFiles.reduce((sum, file) => sum + (file.pageCount || 0), 0);
      const totalPagesAfterUpload = pagesUsed + currentFilesTotalPages;
      
      if (totalPagesAfterUpload > pageLimit) {
        return true;
      }
    }
    
    return false;
  };

  //** countPDFPages **//
  // Count pages in a PDF file using pdf.js
  const countPDFPages = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument(arrayBuffer).promise;
      return pdf.numPages;
    } catch (error) {
      console.error(`Error counting pages for ${file.name}:`, error);
      return 0; // Return 0 if counting fails
    }
  };

  //** processFiles **//
  // Validate and filter uploaded files (size, type, duplicates) and count pages
  const processFiles = async (files) => {
    //? Convert FileList to array and initialize categorization arrays
    const newFilesArray = Array.from(files);
    const validFiles = [];
    const oversizedFiles = [];
    const nonPdfFiles = [];

    //? Categorize files by validation criteria
    newFilesArray.forEach(file => {
      if (file.size > MAX_SIZE_BYTES) {
        oversizedFiles.push(file.name);
      } else if (file.type !== 'application/pdf') {
        nonPdfFiles.push(file.name);
      } else {
        validFiles.push(file);
      }
    });

    //? Build user alert message for rejected files
    let alertMessage = '';
    if (oversizedFiles.length > 0) {
      alertMessage += `The following files are too large (max ${MAX_SIZE_MB} MB) and were not added:\n${oversizedFiles.join('\n')}\n\n`;
    }
    if (nonPdfFiles.length > 0) {
      alertMessage += `The following files are not PDFs and were not added:\n${nonPdfFiles.join('\n')}\n\n`;
    }

    //? Show alert if any files were rejected
    if (alertMessage) {
      window.alert(alertMessage.trim());
    }
    
    //? Count pages for each valid PDF file
    const filesWithPageCounts = await Promise.all(
      validFiles.map(async (file) => {
        const pageCount = await countPDFPages(file);
        // Preserve the original File object and add pageCount property
        const fileWithPageCount = Object.assign(file, { pageCount });
        return fileWithPageCount;
      })
    );
    
    //? Update selected files state, avoiding duplicates
    setSelectedFiles(prevFiles => {
      const updatedFiles = [...prevFiles];
      filesWithPageCounts.forEach(vf => {
        if (!prevFiles.find(pf => pf.name === vf.name)) {
          updatedFiles.push(vf);
        }
      });
      return updatedFiles;
    });

    //? Clear any previous server response
    if (setServerResponse) {
      setServerResponse(null); 
    }
  };

  //** handleFileChange **//
  // Handle file input change event
  const handleFileChange = async (e) => {
    if (e.target.files && e.target.files.length > 0) {
      await processFiles(e.target.files);
      e.target.value = null; 
    }
  };

  //** handleBrowseClick **//
  // Trigger file input click for browse functionality
  const handleBrowseClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  //** handleDragOver **//
  // Handle drag over event for drag-and-drop
  const handleDragOver = (e) => {
    e.preventDefault()
    setIsDragActive(true)
  }

  //** handleDragLeave **//
  // Handle drag leave event for drag-and-drop
  const handleDragLeave = (e) => {
    e.preventDefault()
    setIsDragActive(false)
  }

  //** handleDrop **//
  // Handle file drop event for drag-and-drop
  const handleDrop = async (e) => {
    e.preventDefault()
    setIsDragActive(false)
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      await processFiles(e.dataTransfer.files)
    }
  }

  //** handleRemoveFile **//
  // Remove file from selected files array
  const handleRemoveFile = (fileNameToRemove) => {
    setSelectedFiles(prevFiles => {
      const updatedFiles = prevFiles.filter(file => file.name !== fileNameToRemove);
      // Clear model selection if no files remain
      if (updatedFiles.length === 0) {
        setSelectedModel('');
      }
      return updatedFiles;
    });
  };

  //## ======================= UPLOAD LOGIC ======================= ##//
  
  //** handleUpload **//
  // Main upload function - validates and submits files to backend
  const handleUpload = async () => {

    //? Validate upload prerequisites
    if (selectedFiles.length === 0) {
      window.alert('Please add PDF files to the buffer before uploading.');
      return;
    }

    if (!selectedModel) {
      window.alert('Please select an AI model before uploading.');
      return;
    }

    const hasConsent = localStorage.getItem('dataCollectionConsent') === 'true';

    //? Clear UI state and show success message
    setSelectedFiles([]);
    setSelectedModel('');
    setUploadSuccess(true);
    setTimeout(() => setUploadSuccess(false), 3000);
  
    console.log(`Processing files with model: ${selectedModel}`);

    //? Process all files in parallel using Promise.all
    const uploadPromises = selectedFiles.map(async (file) => {
      try {
        //? Prepare FormData for file upload
        const formData = new FormData();
        formData.append('file', file);
        const res = await authenticatedFetch(
          `/api/upload-pdf?hasConsent=${hasConsent}&model=${selectedModel}`, {
            method: 'POST',
            body: formData,
          }
        );
  
        if (!res.ok) {
          //? Handle upload failure - create error upload object
          const errorUpload = {
            id: `error-${file.name}-${Date.now()}`,
            originalName: file.name,
            createdAt: new Date().toISOString(),
            progress: 0,
            status: 'Failed', // Use 'Failed' to match backend
            fileSize: `${(file.size/1024/1024).toFixed(2)} MB`
          };

          //? Update UI based on current tab filter
          if (statusFilter === 'Error') {
            setUploads(prev => [errorUpload, ...prev]);
          }

          //? Trigger debounced refresh for other tabs
          if (statusFilter === 'Completed') {
            debouncedRefreshCompleted();
          } else if (statusFilter === 'Queued') {
            debouncedRefreshQueued();
          }

          return;
        }
  
        const { uploadId, structuredResult } = await res.json();
  
        //? Handle successful upload - create new upload object
        const newUpload = {
          id: uploadId,
          originalName: file.name,
          createdAt: new Date().toISOString(),
          progress: 0,
          status: 'Queued', // Start as Queued
          fileSize: `${(file.size/1024/1024).toFixed(2)} MB`
        };

        //? Update UI based on current tab filter
        if (statusFilter === 'Queued') {
          setUploads(prev => [newUpload, ...prev]);
        } else {
          //? Trigger debounced refresh for other tabs
          if (statusFilter === 'Completed') {
            debouncedRefreshCompleted();
          } else if (statusFilter === 'Error') {
            debouncedRefreshError();
          }
        }
  
        //? Set server response if callback provided
        if (typeof setServerResponse === 'function') {
          setServerResponse(structuredResult);
        }
      } catch (err) {
        console.error(`Exception during upload of ${file.name}:`, err);
        
        //? Handle different types of errors appropriately
        if (err.message.includes('not authenticated') || 
            err.message.includes('Authentication expired') ||
            err.message.includes('Session expired') ||
            err.message.includes('Authentication failed')) {
          //? Authentication errors - use handleAuthError
          const userMessage = handleAuthError(err, `upload ${file.name}`);
          //? Note: authenticatedFetch will handle redirects automatically
        } else if (err.message.includes('Failed to fetch') || 
                   err.message.includes('NetworkError') ||
                   err.message.includes('fetch')) {
          //? Network errors - user-friendly message
          console.error(`Network error uploading ${file.name}:`, err);
          //? Could show a toast notification: "Network error. Please check your connection."
        } else {
          //? Other errors (server errors, etc.) - generic message
          console.error(`Server error uploading ${file.name}:`, err);
          //? Could show a toast notification: "Upload failed. Please try again."
        }
        
        //? Handle exception - create exception upload object
        const exceptionUpload = {
          id: `exception-${Date.now()}`,
          originalName: file.name,
          createdAt: new Date().toISOString(),
          progress: 0,
          status: 'Failed', // Use 'Failed' to match backend
          fileSize: `${(file.size/1024/1024).toFixed(2)} MB`
        };

        //? Update UI based on current tab filter
        if (statusFilter === 'Error') {
          setUploads(prev => [exceptionUpload, ...prev]);
        }

        //? Trigger debounced refresh for other tabs
        if (statusFilter === 'Completed') {
          debouncedRefreshCompleted();
        } else if (statusFilter === 'Queued') {
          debouncedRefreshQueued();
        }
        
        //? Note: authenticatedFetch will handle redirects automatically
      }
    });

    //? Wait for all uploads to complete processing
    await Promise.all(uploadPromises); 
    
    //? Update page usage after successful uploads
    if (pageLimit > 0 && totalPdfPages > 0) {
      try {
        const response = await authenticatedFetch('/api/user/update-page-usage', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ pages: totalPdfPages })
        });
        
        if (response.ok) {
          const data = await response.json();
          // Update local state with new page usage
          setPagesUsed(data.pagesUsed);
          setRemainingPages(data.remaining);
        } else {
          console.error('Failed to update page usage:', response.statusText);
        }
      } catch (error) {
        console.error('Error updating page usage:', error);
      }
    }
    
    //? Update document usage after successful uploads
    if (documentsLimit > 0 && selectedFiles.length > 0) {
      try {
        const response = await authenticatedFetch('/api/user/update-document-usage', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ documents: selectedFiles.length })
        });
        
        if (response.ok) {
          const data = await response.json();
          // Update local state with new document usage
          setDocumentsUsed(data.documentsUsed);
          setRemainingDocuments(data.remaining);
        } else {
          console.error('Failed to update document usage:', response.statusText);
        }
      } catch (error) {
        console.error('Error updating document usage:', error);
      }
    }
    
    // Note: Counts and data will update automatically via real-time listeners
  };

  //## ======================= UTILITY FUNCTIONS ======================= ##//
  
  //** statusClass **//
  // Return CSS class name based on upload status
  function statusClass(status) {
    if (status === 'Error' || status === 'Failed') return 'statusError';
    if (status === 'Completed')     return 'statusCompleted';
    if (status === 'In-Progress')   return 'statusInProgress';
    return '';
  }

  //** formatFileSize **//
  // Convert bytes to human-readable file size (MB/GB)
  function formatFileSize(size) {
    if (typeof size === 'string') return size; // Already formatted (legacy)
    if (typeof size !== 'number' || isNaN(size)) return 'N/A';
    
    const sizeInMB = size / (1024 * 1024);
    
    if (sizeInMB >= 1024) {
      return `${(sizeInMB / 1024).toFixed(2)} GB`;
    } else {
      return `${sizeInMB.toFixed(2)} MB`;
    }
  }

  //## ======================= RENDER ======================= ##//
  
  return (
    <main className={styles.uploadPage}>  

      {/* NAVBAR */}
      <Navbar />

      {/* MAIN CONTENT: 2 Columns */}
      <section className={styles.mainContent}>
        
        {/* LEFT COLUMN */}
        <div className={styles.leftColumn}>

          {/* --- Upload Zone (top) --- */}
            <div 
                className={`${styles.uploadZone} ${isDragActive ? styles.dragActive : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                <Typography variant="h6" className={styles.zoneTitle}>
                    Upload Zone
                </Typography>

                {selectedFiles.length > 0 ? (
                <p className={styles.fileInfo}>
                    {selectedFiles.length} file(s) selected
                </p>
                ) : (
                <p className={styles.dropzoneText}>
                    Drag &amp; drop PDFs here, or click "Browse" below
                </p>
                )}

                {/* Row with file input + file name + browse button */}
                <div className={styles.uploadRow}>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="application/pdf"
                        className={styles.hiddenFileInput}
                        onChange={handleFileChange}
                        multiple 
                    />

                    <button onClick={handleBrowseClick} className={styles.browseButton}>
                        <p>Browse</p>
                    </button>
                </div>
            </div>

            {/* --- Upload Buffer (bottom) --- */}
            <div className={styles.uploadBuffer}>

                {/* Header row with title and button */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 32, whiteSpace: 'nowrap', minWidth: 0, flexWrap: 'nowrap', flexShrink: 1 }}>

                      {/* Title */}
                      <Typography variant="h6" className={styles.sectionTitle} style={{ lineHeight: 1, margin: 0 }}>
                        Upload Buffer
                      </Typography>
                      
                      {/* Disclaimer next to title */}
                      {/*
                      <span
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          marginLeft: 8,
                          color: '#e67e22',
                          fontSize: 13,
                          whiteSpace: 'nowrap',
                          gap: 4,
                          lineHeight: 1,
                        }}
                      >
                        <WarningIcon fontSize="small" style={{ marginRight: 2 }} />
                        {`Max 20 Files in buffer`}
                      </span>
                      */}
                    </div>

                    <p 
                      className={styles.uploadSuccess} 
                      style={{ visibility: uploadSuccess ? 'visible' : 'hidden' }}
                    >
                      Uploads Initiated!
                    </p>

                    <div className={styles.uploadControls} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>

                      {/* Document Counter Display */}
                      {selectedFiles.length > 0 && (
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '8px 12px',
                          backgroundColor: '#f5f5f5',
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontWeight: '500',
                          color: '#333',
                          whiteSpace: 'nowrap'
                        }}>
                          {/* Warning icon if over document limit */}
                          {documentsLimit > 0 && (documentsUsed + selectedFiles.length) > documentsLimit && (
                            <Tooltip 
                              title={`Current batch goes over document limit: ${documentsUsed + selectedFiles.length}/${documentsLimit}`}
                              arrow
                              placement="top"
                            >
                              <WarningIcon 
                                fontSize="small" 
                                style={{ 
                                  color: '#e67e22', 
                                  marginRight: '6px',
                                  cursor: 'help'
                                }}
                              />
                            </Tooltip>
                          )}
                          {/* Warning icon if any document exceeds 20 pages */}
                          {documentsLimit !== null && validateDocumentPages() && (
                            <Tooltip 
                              title={`Some documents exceed 20-page limit`}
                              arrow
                              placement="top"
                            >
                              <WarningIcon 
                                fontSize="small" 
                                style={{ 
                                  color: '#e67e22', 
                                  marginRight: '6px',
                                  cursor: 'help'
                                }}
                              />
                            </Tooltip>
                          )}
                          Total: {selectedFiles.length} documents
                        </div>
                      )}

                      {/* Model Select Dropdown */}
                      <select 
                        value={selectedModel} 
                        onChange={(e) => setSelectedModel(e.target.value)}
                        className={styles.modelSelect}
                      >
                        <option value="" disabled>Select AI Model</option>
                        <option value="shipping-labels" disabled>[SL] Shipping Labels (Unavailable)</option>
                        <option value="safety-datasheets">[SDS] Safety Datasheets</option>
                      </select>
                      
                      {/* Upload Button */}
                      <button 
                        onClick={handleUpload} 
                        className={`${styles.uploadButton} ${isUploadDisabledByDocuments() ? styles.uploadButtonDisabled : ''}`}
                        disabled={isUploadDisabledByDocuments()}
                      >
                        <UploadFileIcon className={styles.uploadIcon} />
                        <p>Upload Files</p>
                      </button>
                    </div>
                </div>

                {/* Table populated via a loop */}
                <table className={styles.stripedTable}>
                    <thead>
                        <tr>
                            <th style={{width: '80%'}}>
                                File Name{selectedFiles.length > 0 ? ` - ${selectedFiles.length} Document(s)` : ''}
                            </th>
                            <th style={{width: '14%'}}>Size</th>
                            <th style={{width: '5%', textAlign: 'center'}}>Pages</th>
                            <th style={{display: 'table-cell', width: '1%', minWidth: 60, textAlign: 'center'}}>Remove</th>
                        </tr>
                    </thead>
                    <tbody>
                        {selectedFiles.map((file, index) => (
                            <tr key={file.name + index}>
                                <td style={{fontWeight: 500, fontSize: '1.05rem', maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                                  {file.name}
                                  {!isAdmin && documentsLimit !== null && (file.pageCount || 0) > MAX_PAGES_PER_DOCUMENT && (
                                    <Tooltip 
                                      title="Max of 20 pages per document"
                                      arrow
                                      placement="top"
                                    >
                                      <WarningIcon 
                                        fontSize="small" 
                                        style={{ 
                                          color: '#e67e22', 
                                          marginLeft: '8px',
                                          cursor: 'help',
                                          verticalAlign: 'middle'
                                        }}
                                      />
                                    </Tooltip>
                                  )}
                                </td>
                                <td>{formatFileSize(file.size)}</td>
                                <td style={{textAlign: 'center', fontSize: '14px', fontWeight: '500'}}>
                                    {file.pageCount ? file.pageCount : 'N/A'}
                                </td>
                                <td style={{display: 'flex', alignItems: 'center', justifyContent: 'center', width: '1%', minWidth: 60}}>
                                    <button onClick={() => handleRemoveFile(file.name)} className={styles.removeIconBtn}>
                                        <ClearIcon />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

        </div>

        {/* Progress Tracker */}
        <div className={styles.rightColumn}>
          <div className={styles.progressHeader}>
            <Typography variant="h6" className={styles.sectionTitle}>
              Progress Tracker
            </Typography>
            <div className={styles.headerControls}>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className={styles.statusFilter}
              >
                <option value="In-Progress">In-Progress ({uploadCounts.byStatus?.['In-Progress'] || 0})</option>
                <option value="Completed">Completed ({uploadCounts.byStatus?.['Completed'] || 0})</option>
                <option value="Error">Error ({uploadCounts.byStatus?.['Error'] || 0})</option>
                <option value="Queued">Queued ({uploadCounts.byStatus?.['Queued'] || 0})</option>
              </select>
              <div className={styles.paginationControls}>
                <button
                  className={styles.pageArrow}
                  onClick={() => handlePageChange(currentTablePage - 1)}
                  disabled={currentTablePage === 0}
                  aria-label="Previous Page"
                >
                  <KeyboardArrowLeftIcon />
                </button>
                <span className={styles.pageInfo}>
                  Page {currentTablePage + 1} of {totalTablePages || 1}
                </span>
                <button
                  className={styles.pageArrow}
                  onClick={() => handlePageChange(currentTablePage + 1)}
                  disabled={currentTablePage >= totalTablePages - 1}
                  aria-label="Next Page"
                >
                  <KeyboardArrowRightIcon />
                </button>
              </div>
            </div>
          </div>
          <table className={styles.stripedTable}>
            <thead>
              <tr>
                <th>File Name</th>
                <th>File Size</th>
                <th>Uploaded At</th>
                <th>Status</th>
                <th>Live Progress</th>
              </tr>
            </thead>
            <tbody>
              {uploads.length === 0 ? (
                <tr>
                  <td colSpan={5} className={styles.emptyState}>
                    No uploads found.
                  </td>
                </tr>
              ) : (
                uploads.map((u) => (
                  <tr key={u.id}>
                    <td>{u.originalName}</td>
                    <td>{formatFileSize(u.fileSize)}</td>
                    <td>{new Date(u.createdAt).toLocaleString()}</td>
                    <td className={styles[statusClass(u.status)]}>{u.status || 'N/A'}</td>
                    <td>
                      <div className={styles.progressBar}>
                        <div 
                          className={`${styles.progress} ${u.status === 'Error' ? styles.progressError : ''}`} 
                          style={{ width: `${u.progress || 0}%` }} 
                        />
                        <span className={styles.progressText}>
                          {u.status === 'Error' ? 'Error' : `${u.progress || 0}%`}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

      </section>
      
      {/* Paywall Modal - Shows for unsubscribed users */}
      {showPaywallModal && <PaywallModal />}
    </main>
  )
}

export default UploadPage